import videoOverVideoModule from './videoOverVideoModule.js';
import imageModule from './imageModule.js';
import parallaxVideoModule from './parallaxVideoModule.js';
import imageCarouselModule from './imageCarouselModule.js';

const filmstrip = {
  update: null,
  init() {
    const container = document.querySelector('.pages-container');
    const pages = Array.from(container?.querySelectorAll('.filmstrip-page') || []);
    if (!container || (!pages.length && !videoOverVideoModule.scenes.length)) return;
    const desktop = matchMedia('(min-width: 768px)');
    const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
    const getStepDistance = page => desktop.matches
      ? container.clientWidth * 0.425
      : container.clientHeight * 0.3;
    const update = () => {
      const viewport = container.getBoundingClientRect();
      const scenes = [];
      for (const page of pages) {
        const stage = page.querySelector('.filmstrip-stage');
        const bounds = page.getBoundingClientRect();
        const travel = Number(page.dataset.filmstripSteps) * getStepDistance(page);
        const pinOffset = desktop.matches ? (container.clientWidth - stage.offsetWidth) / 2 : 0;
        const consumed = desktop.matches ? viewport.left + pinOffset - bounds.left : viewport.top - bounds.top;
        scenes.push({ page, travel, consumed: Math.max(0, Math.min(travel, consumed)) });
        const progress = Math.max(0, Math.min(Number(page.dataset.filmstripSteps), consumed / (travel / Number(page.dataset.filmstripSteps))));
        const track = page.querySelector('.filmstrip-track');
        const rail = page.querySelector('.filmstrip-rail');
        const trackLength = desktop.matches ? track.offsetHeight : track.offsetWidth;
        const trackTravel = Math.max(0, trackLength - (desktop.matches ? rail.clientHeight : rail.clientWidth));
        page.style.setProperty('--filmstrip-track-length', `${trackLength}px`);
        track.style.transform = reducedMotion.matches ? 'none' : `translate${desktop.matches ? 'Y' : 'X'}(${-progress * trackTravel / Number(page.dataset.filmstripSteps)}px)`;
        stage.dataset.progress = String(progress);
      }
      videoOverVideoModule.update();
      for (const scene of videoOverVideoModule.getScenes()) {
        const bounds = scene.page.getBoundingClientRect();
        const consumed = desktop.matches ? viewport.left - bounds.left : viewport.top - bounds.top;
        scenes.push({ page: scene.page, travel: scene.travel, consumed: Math.max(0, Math.min(scene.travel, consumed)) });
      }
      for (const bookPage of container.querySelectorAll('.pages-inner > .page, .pages-inner > .page-border')) {
        if (bookPage.classList.contains('filmstrip-page') || videoOverVideoModule.scenes.some(scene => scene.page === bookPage)) continue;
        if (!desktop.matches) {
          bookPage.style.translate = 'none';
          continue;
        }
        let compensation = 0;
        for (const scene of scenes) {
          const followsScene = Boolean(scene.page.compareDocumentPosition(bookPage) & Node.DOCUMENT_POSITION_FOLLOWING);
          compensation += scene.consumed - (followsScene ? scene.travel : 0);
        }
        bookPage.style.translate = desktop.matches ? `${compensation}px 0` : `0 ${compensation}px`;
      }
      imageModule.update?.();
      parallaxVideoModule.update?.();
      imageCarouselModule.update?.();
    };
    this.update = update;
    const layout = () => {
      for (const page of pages) {
        const ratio = Number.parseFloat(page.style.getPropertyValue('--filmstrip-ratio')) || 1;
        const width = desktop.matches ? Math.min(container.clientWidth, container.clientHeight * ratio) : container.clientWidth;
        const height = container.clientHeight;
        const travel = Number(page.dataset.filmstripSteps) * getStepDistance(page);
        const frameGap = (desktop.matches ? height : width) * 0.018;
        const overscan = (desktop.matches ? height : width) * 0.02 + 2;
        const frameHeight = desktop.matches ? (height + overscan * 2 - frameGap * 4) / 5 : width / 3.5 / 1.5;
        const trackWidth = desktop.matches ? frameHeight * 1.5 / 0.76 : frameHeight / 0.68;
        page.style.setProperty('--filmstrip-overscan', `${overscan}px`);
        page.style.setProperty('--filmstrip-width', `${width}px`);
        page.style.setProperty('--filmstrip-pin-left', `${desktop.matches ? (container.clientWidth - width) / 2 : 0}px`);
        page.style.setProperty('--filmstrip-height', `${height}px`);
        page.style.setProperty('--filmstrip-travel', `${travel}px`);
        page.style.setProperty('--filmstrip-frame-gap', `${frameGap}px`);
        page.style.setProperty('--filmstrip-frame-height', `${frameHeight}px`);
        page.style.setProperty('--filmstrip-track-width', `${trackWidth}px`);
        page.style.setProperty('--filmstrip-edge-padding', `${trackWidth * 0.16}px`);
      }
      update();
    };
    container.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', layout);
    desktop.addEventListener('change', layout);
    reducedMotion.addEventListener('change', update);
    for (const image of container.querySelectorAll('.filmstrip-page img')) image.addEventListener('load', update);
    new ResizeObserver(layout).observe(container);
    layout();
  },
};

export default filmstrip;